import { Injectable, NgZone } from '@angular/core';
import { Location } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { UtilService } from './util.service';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CapacitorPlatformService {
  private backButtonListener?: PluginListenerHandle;
  private appUrlListener?: PluginListenerHandle;
  private routeDepth = 0;

  constructor(
    private location: Location,
    private router: Router,
    private zone: NgZone,
    private utilService: UtilService,
    private dialog: MatDialog
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.routeDepth += 1;
      }
    });
  }

  init(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    document.body.classList.toggle('native-ios', Capacitor.getPlatform() === 'ios');

    CapacitorApp.addListener('backButton', () => {
      this.zone.run(() => {
        if (this.isPaymentGatewayPopupActive()) {
          return;
        }

        if (this.closeOpenMaterialDialogs() || this.closeOpenBootstrapModal()) {
          this.goBackToPreviousScreen();
          return;
        }

        if (this.routeDepth > 1) {
          this.goBackToPreviousScreen();
          return;
        }

        CapacitorApp.exitApp();
      });
    }).then(listener => {
      this.backButtonListener = listener;
    });

    CapacitorApp.addListener('appUrlOpen', event => this.handlePaymentReturn(event.url))
      .then(listener => {
        this.appUrlListener = listener;
        return CapacitorApp.getLaunchUrl();
      })
      .then(launch => {
        // A WebView reload can retain an older launch intent; only consume it on initial entry.
        if (launch?.url && (window.location.pathname === '/' || window.location.pathname === '/index.html')) {
          this.handlePaymentReturn(launch.url, false);
        }
      });
  }

  private handlePaymentReturn(returnUrl: string, reload = true): void {
      this.zone.run(() => {
        try {
          const url = new URL(returnUrl);
          const route = `${url.pathname}${url.search}`;
          const returnHosts = new Set([
            'bookingdexter.com', 'www.bookingdexter.com', 'dev.bookingdexter.com'
          ]);
          if (environment.apiBaseUrl) {
            returnHosts.add(new URL(environment.apiBaseUrl).hostname);
          }
          const isBookingDexterUrl =
            ((url.protocol === 'bookingdexter:' || url.protocol === 'https:') && returnHosts.has(url.hostname)) ||
            (url.protocol === 'https:' && url.hostname === 'localhost');

          if (isBookingDexterUrl && (
            url.pathname === '/transaction/payment/billdesk/confirmation' ||
            /^\/transaction\/payment\/status\/[^/]+$/.test(url.pathname)
          )) {
            Browser.close().catch(() => undefined);
            this.utilService.clearBillDeskUiState();
            // Reload the bundled app to remove any SDK iframe still covering the status screen.
            if (reload) {
              window.location.replace(route);
            } else {
              this.router.navigateByUrl(route, { replaceUrl: true });
            }
          }
        } catch (error) {
          console.log('Unable to handle app URL open', error);
        }
      });
  }

  destroy(): void {
    if (this.appUrlListener) {
      this.appUrlListener.remove();
      this.appUrlListener = undefined;
    }
    if (this.backButtonListener) {
      this.backButtonListener.remove();
      this.backButtonListener = undefined;
    }
  }

  isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  private closeOpenBootstrapModal(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }

    const openModal = document.querySelector('.modal.show') as HTMLElement | null;

    if (!openModal) {
      return false;
    }

    const jquery = (window as any).$;

    if (jquery) {
      jquery(openModal).modal('hide');
    } else {
      openModal.classList.remove('show');
      openModal.style.display = 'none';
      openModal.setAttribute('aria-hidden', 'true');
      openModal.removeAttribute('aria-modal');
    }

    this.utilService.clearBootstrapModalState();
    return true;
  }

  private closeOpenMaterialDialogs(): boolean {
    if (!this.dialog.openDialogs.length) {
      return false;
    }

    this.dialog.closeAll();
    return true;
  }

  private goBackToPreviousScreen(): void {
    if (this.routeDepth <= 1) {
      return;
    }

    this.routeDepth -= 1;
    this.location.back();
  }

  private isPaymentGatewayPopupActive(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }

    return document.body.classList.contains('billdesk-sdk-active') ||
      document.body.classList.contains('billdesk-payment-active');
  }

  openExternalUrl(url: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      window.location.href = url;
      return Promise.resolve();
    }

    return Browser.open({ url }).then(() => undefined);
  }
}
